import React, { useReducer, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useGamificationService } from '../services/gamificationService';
import type { QuestType } from '../types';

interface WizardForm {
  title: string;
  type: QuestType;
  description: string;
  customer_id: string;
  priority: string;
  deadline: string;
}

type FormAction =
  | { type: 'SET_FIELD'; field: keyof WizardForm; value: string };

function formReducer(state: WizardForm, action: FormAction): WizardForm {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
}

const INITIAL_FORM: WizardForm = {
  title: '',
  type: 'improvement',
  description: '',
  customer_id: '',
  priority: 'normal',
  deadline: '',
};

const QUEST_TYPES: { value: QuestType; label: string }[] = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'investigation', label: 'Investigation' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'challenge', label: 'Challenge' },
  { value: 'boss_battle', label: 'Boss Battle' },
];

interface QuestCreationWizardProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
}

const QuestCreationWizard: React.FC<QuestCreationWizardProps> = ({ open, onClose, customerId }) => {
  const service = useGamificationService();
  const [form, dispatch] = useReducer(formReducer, { ...INITIAL_FORM, customer_id: customerId ?? '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const titleError = hasAttemptedSave && !form.title.trim() ? 'Title is required' : undefined;

  const handleSave = async () => {
    setHasAttemptedSave(true);
    if (!form.title.trim()) return;

    setSaving(true);
    setError(null);
    try {
      await service.configureQuest({
        title: form.title,
        type: form.type,
        description: form.description || undefined,
        customer_id: form.customer_id || undefined,
        priority: form.priority,
        deadline: form.deadline || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create quest');
    } finally {
      setSaving(false);
    }
  };

  const field = (f: keyof WizardForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: 'SET_FIELD', field: f, value: e.target.value });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalHeader icon={AssignmentIcon} title="Create Quest" onClose={onClose} />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Provide a high-level description. The AI Game Master will generate tasks and narrative.
        </Typography>

        {error && <InlineAlert severity="error" sx={{ mb: 2 }}>{error}</InlineAlert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Quest title"
            value={form.title}
            onChange={field('title')}
            required
            error={!!titleError}
            helperText={titleError}
            fullWidth
          />
          <TextField
            label="Type"
            value={form.type}
            onChange={field('type')}
            select
            fullWidth
          >
            {QUEST_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description (optional)"
            value={form.description}
            onChange={field('description')}
            multiline
            rows={3}
            fullWidth
            placeholder="Give the AI context — what needs to be done and why."
          />
          <TextField
            label="Priority"
            value={form.priority}
            onChange={field('priority')}
            select
            fullWidth
          >
            {['low', 'normal', 'high', 'critical'].map((p) => (
              <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Deadline (optional)"
            value={form.deadline}
            onChange={field('deadline')}
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={16} /> : 'Create Quest'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestCreationWizard;
